import { ref, toValue } from 'vue';
import { storeToRefs } from 'pinia';
import { Track } from '../types';
import { createHighlightRect, getActualLineHeight, getAllTextNodes, measureTextLines } from '../util/epubJs/epub-dom';
import useRenditionStore from '../stores/rendition';
import useMediaStore from '../stores/media';
import { EpubJsRendition, EpubJsView } from '../types/epubjs';
import useSettingStore from '../stores/setting';
import { Contents } from 'epubjs';
import { replaceCfiSpinePath } from '../util/location';

// Use "highlight" as the type since epub.js only recognizes: "highlight", "underline", "mark"
// We differentiate using className instead
export const AUDIO_HIGHLIGHT_TYPE = 'highlight';
export const AUDIO_HIGHLIGHT_ACTIVE_CLASS = 'active-audio-highlight';

const getViews = (rendition?: EpubJsRendition | undefined): EpubJsView[] => {
  if (!rendition) return [];
  return (rendition?.manager?.views?._views as EpubJsView[]) || [];
};

const getDocumentForSpine = (rendition: EpubJsRendition | undefined, spineIndex: number): Document | null => {
  const views = getViews(rendition);
  const view = views.find((v: EpubJsView) => v.section?.index === spineIndex);
  return view?.document || null;
};

// Finds the view document whose section href matches the given srcFile path.
// Used as a fallback when getDocumentForSpine() returns null (view not yet indexed).
const getDocumentBySrcFile = (rendition: EpubJsRendition | undefined, srcFile: string): Document | null => {
  const normalizedSrc = srcFile.replace(/^\.\.\//, '');
  const views = getViews(rendition);
  const view = views.find((v: EpubJsView) => {
    const href = v.section?.href || '';
    return href === normalizedSrc || href.endsWith(normalizedSrc) || normalizedSrc.endsWith(href);
  });
  return view?.document || null;
};

// Accepts an optional document so we can bypass the heavy getViews loop when we already know the target document.
// When providedDoc is given and the element is not found there, returns null immediately — never scans other
// iframes. This prevents duplicate IDs across continuous-book spine items from causing cross-iframe matches.
const findElement = (rendition: EpubJsRendition | undefined, id: string, providedDoc?: Document) => {
  if (providedDoc) {
    const el = providedDoc.getElementById(id);
    if (el) {
      return { element: el as HTMLElement, doc: providedDoc };
    }
    return null;
  }

  const views = getViews(rendition);
  for (const view of views) {
    const el = view.document?.getElementById(id);
    if (el) return { element: el as HTMLElement, doc: view.document as Document };
  }
  return null;
};

const currentPlayingTrackId = ref<string | null>(null);

// Track abort controllers per spine index to allow multiple sections to have handlers simultaneously
const clickAborters = new Map<number, AbortController>(); // spineIndex -> AbortController

/**
 * Composable for managing audio track highlighting using epub.js annotations API
 *
 * - Uses epub.js native annotations instead of custom DOM manipulation
 * - Provides precise multi-line highlighting with per-line width measurement
 * - Handles complex DOM structures with multiple text nodes
 * - Dynamically calculates line height based on actual font size
 * - Highlight appearance is controlled by the settings store (globally accessible and persisted)
 */
const useAudioAnnotations = () => {
  const { rendition, hasBackgroundOverride } = storeToRefs(useRenditionStore());
  const mediaStore = useMediaStore();
  const { activeHighlightCfi } = storeToRefs(mediaStore);
  const { setActiveHighlightCfi } = mediaStore;
  const settingStore = useSettingStore();
  const { hasAudioTracking, audioHighlightColor, audioHighlightOpacity, audioHighlightBlendMode } =
    storeToRefs(settingStore);

  /**
   * Converts element-only CFI to text range CFI.
   */
  const convertElementCfiToRange = (
    elementCfi: string,
    elementId: string,
    providedDoc?: Document,
  ): { rangeCfi: string; element: HTMLElement; document: Document } | null => {
    const r = toValue(rendition);
    if (!r) return null;

    const found = findElement(r as any, elementId, providedDoc);

    if (!found) {
      console.warn(`Element ${elementId} not found for CFI ${elementCfi}`);
      return null;
    }

    try {
      // Get all text nodes
      const textNodes = getAllTextNodes(found.element);
      if (textNodes.length === 0) {
        console.warn(`No text nodes found in element ${elementId}`);
        return null;
      }

      // Create a range that encompasses all text in the element
      const range = found.doc.createRange();
      range.selectNodeContents(found.element);

      // Find first and last text nodes for precise range
      const firstTextNode = textNodes[0];
      const lastTextNode = textNodes[textNodes.length - 1];

      range.setStart(firstTextNode, 0);
      range.setEnd(lastTextNode, lastTextNode.textContent?.length || 0);

      // Use epub.js to generate the CFI from the range
      const activeContents = r.getContents() as unknown as Contents[];
      const section = activeContents.find((c) => c.document === found.doc);
      if (!section) {
        console.warn(`Could not find section for element ${elementId}`);
        return null;
      }

      // In continuous books, section.cfiFromRange() may use the wrong section's spine index
      // Replace the generated spine path with the one from the original element CFI
      // If range has correct spine, this will be a no-op and return the original CFI
      const rangeCfi = replaceCfiSpinePath(section.cfiFromRange(range), elementCfi);

      return { rangeCfi: rangeCfi, element: found.element, document: found.doc };
    } catch (error) {
      console.warn(`Failed to convert element CFI to range for ${elementId}:`, error);
      return null;
    }
  };

  /**
   * Enhances a standard EPUB highlight with pixel-perfect multi-line rectangles.
   * Uses intelligent blend mode selection based on whether the background has been overridden.
   */
  const applyMultiLineStyle = (container: SVGGElement, element: HTMLElement, doc: Document): void => {
    const elementRect = element.getBoundingClientRect();
    const lineHeight = getActualLineHeight(element, doc);
    const estimatedLines = Math.round(elementRect.height / lineHeight);

    // ALWAYS remove all default epub.js rects first to clear the slate
    container.querySelectorAll('rect').forEach((rect) => rect.remove());
    // Strip any attributes epub.js may have placed on the parent <g>.
    // These force an offscreen compositing layer in Safari (the "fog" effect).
    container.removeAttribute('fill');
    container.removeAttribute('fill-opacity');
    container.removeAttribute('mix-blend-mode');
    // Remove CSS blend-mode entirely (setting to '' removes the property; 'normal' would keep it)
    container.style.mixBlendMode = '';
    container.style.visibility = '';

    // Determine appropriate blend mode:
    // - If background IS overridden (we control it) → use user's audioHighlightBlendMode preference
    // - If background is NOT overridden (fixed layout) → use 'multiply' as safe default
    const effectiveBlendMode = hasBackgroundOverride.value ? audioHighlightBlendMode.value : 'multiply';

    if (estimatedLines > 1) {
      // Multi-line text - measure precisely
      console.debug(`Multi-line text: ~${estimatedLines} lines`);

      // Measure actual lines
      const lines = measureTextLines(element, doc);

      if (lines.length > 0) {
        // Create a precise rect for each visible line
        lines.forEach((line) => {
          const rect = createHighlightRect(
            line.startX,
            line.y,
            line.endX - line.startX,
            lineHeight,
            audioHighlightColor.value,
            audioHighlightOpacity.value,
            effectiveBlendMode,
          );
          container.appendChild(rect);
        });
      } else {
        // Fallback: visible-area-clipped full-width rects
        for (let i = 0; i < estimatedLines; i++) {
          const rect = createHighlightRect(
            elementRect.left,
            elementRect.top + i * lineHeight,
            elementRect.width,
            lineHeight,
            audioHighlightColor.value,
            audioHighlightOpacity.value,
            effectiveBlendMode,
          );
          container.appendChild(rect);
        }
      }
    } else {
      // Single line - Generate exactly ONE clean rect covering the visible part of the element
      const singleRect = createHighlightRect(
        elementRect.left,
        elementRect.top,
        elementRect.width,
        elementRect.height,
        audioHighlightColor.value,
        audioHighlightOpacity.value,
        effectiveBlendMode,
      );
      container.appendChild(singleRect);
    }
  };

  /**
   * Highlights the currently playing audio track.
   */
  const highlightPlayingTrack = (track: Track | undefined): void => {
    const r = toValue(rendition);

    // Check if the setting is actually enabled before drawing!
    if (!hasAudioTracking.value || !r?.annotations || !track?.cfi) {
      return;
    }

    // If we are already highlighting this exact track, do nothing!
    if (currentPlayingTrackId.value === track.text.srcFragmentId) {
      return;
    }

    // Set the tracker IMMEDIATELY to prevent concurrent calls
    currentPlayingTrackId.value = track.text.srcFragmentId;

    // Clear any existing highlight (but we've already set the new ID above)
    if (activeHighlightCfi.value && activeHighlightCfi.value !== track.cfi) {
      try {
        // @ts-ignore
        r.annotations.remove(activeHighlightCfi.value, AUDIO_HIGHLIGHT_TYPE);
      } catch (error) {
        console.warn('Failed to clear previous highlight:', error);
      }
    }

    try {
      console.debug('Audio highlighting track:', track.text.srcFragmentId);

      // Use helper to resolve CFI and get element/document
      const resolved = resolveTrackCfiWithElement(track);
      if (!resolved || !resolved.cfi) {
        console.warn(`Could not resolve CFI for track ${track.text.srcFragmentId}`);
        return;
      }

      const { cfi: cfiToHighlight, element: foundElement, document: foundDoc } = resolved;

      // Validate the element and range before highlighting
      if (foundElement && foundDoc) {
        if (!foundElement.isConnected || !foundElement.textContent?.trim()) {
          console.warn(`Element ${track.text.srcFragmentId} is not in DOM or has no content`);
          return;
        }

        // Validate range can be created
        try {
          const textNodes = getAllTextNodes(foundElement);
          if (textNodes.length === 0) {
            console.warn(`No text nodes found in element ${track.text.srcFragmentId}`);
            return;
          }
          const testRange = foundDoc.createRange();
          testRange.setStart(textNodes[0], 0);
          testRange.setEnd(textNodes[0], Math.min(1, textNodes[0].length));
        } catch (error) {
          console.warn(`Cannot create valid range for element ${track.text.srcFragmentId}:`, error);
          return;
        }
      }

      let annotation: any;
      try {
        // Pass empty data: epub.js would put fill/opacity/mix-blend-mode on the parent <g>,
        // which forces an offscreen compositing layer in Safari (the "fog" effect).
        // We apply those styles ourselves in applyMultiLineStyle after the RAF.
        // @ts-ignore
        annotation = r.annotations.highlight(cfiToHighlight, undefined, undefined, AUDIO_HIGHLIGHT_ACTIVE_CLASS, {});
      } catch (err) {
        console.warn(`Failed to create highlight annotation for ${track.text.srcFragmentId}:`, err);
        return;
      }

      // Verify annotation was created successfully
      if (!annotation || !annotation.mark) {
        console.warn(`Highlight annotation not created for ${track.text.srcFragmentId}`);
        return;
      }

      setActiveHighlightCfi(cfiToHighlight);

      // Enhance the highlight with precise multi-line rectangles.
      // Hide the container for one frame so epub.js's default <g> attributes never
      // reach Safari's compositor (which would produce the "fog" / offscreen-bitmap effect).
      const markElement = annotation.mark?.element;
      if (markElement && foundElement && foundDoc) {
        markElement.style.visibility = 'hidden';
        requestAnimationFrame(() => {
          if (markElement) {
            applyMultiLineStyle(markElement, foundElement, foundDoc);
          }
        });
      }
    } catch (error) {
      console.error(`Failed to highlight track ${track?.text.srcFragmentId}:`, error);
    }
  };

  const clearAllAnnotations = () => {
    clearPlayingHighlight();
  };

  const clearPlayingHighlight = (): void => {
    const r = toValue(rendition);
    if (!activeHighlightCfi.value) return;

    try {
      if (r?.annotations?.remove) {
        r.annotations.remove(activeHighlightCfi.value, AUDIO_HIGHLIGHT_TYPE);
      }
    } catch (error) {
      console.warn('Failed to clear playing highlight:', error);
    } finally {
      setActiveHighlightCfi(null);
      currentPlayingTrackId.value = null;
    }
  };

  /**
   * Adds direct DOM click handlers safely.
   */
  const addTrackClickHandlers = (
    tracks: Track[],
    onTrackClick: (track: Track) => void,
    spineIndex?: number,
    contentsDocument?: Document,
  ): void => {
    const targetSpineIndex = spineIndex ?? (tracks[0] ? getSpineIndexFromTrack(tracks[0]) : undefined);
    if (targetSpineIndex === undefined) return;

    // FIX: DO NOT ABORT! Retrieve the existing controller for this spine, or create a new one.
    // This allows Page 1 and Page 2 to safely share the same lifecycle without killing old listeners.
    let aborter = clickAborters.get(targetSpineIndex);
    if (!aborter || aborter.signal.aborted) {
      aborter = new AbortController();
      clickAborters.set(targetSpineIndex, aborter);
    }
    const { signal } = aborter;

    // Use the helper to avoid duplicate logic
    const targetDoc = contentsDocument || getDocumentForSpine(toValue(rendition) as any, targetSpineIndex);

    if (!targetDoc) {
      console.debug(`Document not ready for spine ${targetSpineIndex}`);
      return;
    }

    tracks.forEach((track) => {
      const element = targetDoc!.getElementById(track.text.srcFragmentId);

      // Use a dataset flag to prevent duplicate bindings when turning pages lazily
      if (element && element.dataset.audioClickBound !== 'true') {
        element.style.cursor = 'pointer';
        element.dataset.audioClickBound = 'true';

        element.addEventListener(
          'click',
          (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            onTrackClick(track);
          },
          { signal },
        );
      }
    });
  };

  const getSpineIndexFromTrack = (track: Track): number | undefined => {
    if (!track.cfi) return undefined;
    const match = track.cfi.match(/^epubcfi\(\/6\/(\d+)/);
    if (match) {
      return Math.floor(parseInt(match[1], 10) / 2) - 1;
    }
    return undefined;
  };

  const resolveTrackCfiWithElement = (
    track: Track,
  ): { cfi: string; element: HTMLElement; document: Document } | null => {
    if (!track.cfi) return null;

    const isElementCfi = track.cfi.endsWith(')') && !track.cfi.includes(',');

    if (isElementCfi) {
      const idMatch = track.cfi.match(/\[([^\]]+)\]\)$/);
      const elementId = idMatch ? idMatch[1] : track.text.srcFragmentId;

      // Isolate the exact document for this track's chapter.
      // Two-layer guard: spine index (fast) → srcFile path match (fallback when index not yet set).
      // If neither resolves, convertElementCfiToRange gets undefined and findElement will scan all
      // iframes — but that path is now safe only when no providedDoc is given.
      const spineIndex = getSpineIndexFromTrack(track);
      const r = toValue(rendition);
      const targetDoc =
        (r && spineIndex !== undefined ? getDocumentForSpine(r as any, spineIndex) : null) ||
        (r ? getDocumentBySrcFile(r as any, track.text.srcFile) : null);

      const converted = convertElementCfiToRange(track.cfi, elementId, targetDoc ?? undefined);

      if (converted) {
        return { cfi: converted.rangeCfi, element: converted.element, document: converted.document };
      }
    }

    return { cfi: track.cfi, element: null as any, document: null as any };
  };

  /**
   * Instantly kills all click listeners for given tracks and restores cursors.
   */
  const removeTrackClickHandlers = (tracks: Track[]): void => {
    const r = toValue(rendition);
    if (!r) return;

    // Group by spine to avoid finding the document over and over
    const tracksBySpine = new Map<number, Track[]>();
    tracks.forEach((track) => {
      const spineIndex = getSpineIndexFromTrack(track);
      if (spineIndex !== undefined) {
        if (!tracksBySpine.has(spineIndex)) tracksBySpine.set(spineIndex, []);
        tracksBySpine.get(spineIndex)!.push(track);
      }
    });

    tracksBySpine.forEach((spineTracks, spineIndex) => {
      // Abort controllers for this spine
      const aborter = clickAborters.get(spineIndex);
      if (aborter) {
        aborter.abort();
        clickAborters.delete(spineIndex);
      }

      // Get the specific document once per spine
      const targetDoc = getDocumentForSpine(r as any, spineIndex);
      if (targetDoc) {
        spineTracks.forEach((track) => {
          const element = targetDoc.getElementById(track.text.srcFragmentId);
          if (element) {
            element.style.cursor = '';
            delete element.dataset.audioClickBound; // Clear the dataset flag!
          }
        });
      }
    });
  };

  /**
   * Adds click handlers for all currently loaded iframes.
   * Useful for reacting to setting changes at runtime (toggling ON).
   */
  const addClickHandlersForAllLoadedIframes = (onTrackClick: (track: Track) => void): void => {
    const r = toValue(rendition);
    if (!r) return;

    // Get all currently loaded content/iframes safely
    const contents = r.getContents() as unknown as Contents[];
    const mediaStore = useMediaStore();

    // Use currentPageTracks if you only want to bind what is visible,
    // or tracks if you want to bind the whole buffer.
    const activeTracks = mediaStore.currentPageTracks || [];

    if (activeTracks.length === 0) return;

    contents.forEach((content) => {
      const spineIndex = content.sectionIndex;
      if (spineIndex === undefined) return;

      // Filter tracks for this specific spine
      const tracksForSpine = activeTracks.filter((track) => getSpineIndexFromTrack(track) === spineIndex);

      if (tracksForSpine.length > 0) {
        addTrackClickHandlers(tracksForSpine, onTrackClick, spineIndex, content.document);
      }
    });
  };

  /**
   * Instantly kills ALL click listeners across the entire book globally.
   * Useful for reacting to setting changes at runtime (toggling OFF).
   */
  const removeClickHandlersForAllLoadedIframes = (): void => {
    // Abort all listeners globally in one go
    clickAborters.forEach((aborter) => aborter.abort());
    clickAborters.clear();

    const r = toValue(rendition);
    if (!r) return;

    // INSTANT DOM CLEANUP: Don't loop through tracks!
    // Just find any element we flagged and wipe it.
    const contents = r.getContents() as unknown as Contents[];
    contents.forEach((content) => {
      // Target only the elements we modified
      const boundElements = content.document.querySelectorAll('[data-audio-click-bound="true"]');

      boundElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        htmlEl.style.cursor = ''; // Remove the pointer cursor
        delete htmlEl.dataset.audioClickBound; // Remove the flag
      });
    });
  };

  const destroy = (): void => {
    clearPlayingHighlight();
  };

  return {
    addTrackClickHandlers,
    removeTrackClickHandlers,
    addClickHandlersForAllLoadedIframes,
    removeClickHandlersForAllLoadedIframes,
    clearAllAnnotations,
    highlightPlayingTrack,
    clearPlayingHighlight,
    destroy,
  };
};

export default useAudioAnnotations;
